import React, { useRef } from 'react'
import { Card, Col, Container, Row } from 'reactstrap'
import PaperCardHeader from './cards/cardHeader'
import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import {
    MaterialReactTable,
    // createRow,
    type MRT_ColumnDef,
    MRT_ColumnFiltersState,
    type MRT_Row,
    type MRT_TableOptions,
    useMaterialReactTable,
} from "material-react-table";
import { Box, Button, IconButton, TextField, Tooltip } from "@mui/material";
import {
    QueryClient,
    QueryClientProvider,
    useMutation,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import { type User, fakeData, usStates, citiesList, usStateList } from "./makeData";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import axiosInstance from '../../../Utils/axiosInstance';
import { backendbaseUrl } from '../../../Utils/baseUrl';
import { showDeleteConfirmModal } from '../../ConfirmModal';
import { SortingState } from '@tanstack/table-core';

function HolidayComponent() {

    const [sortingState, setSortingState] = useState<SortingState>([]);
    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);

    const [trackFilter, setTrackFilter] = useState(false)
    const [validationErrors, setValidationErrors] = useState<
        Record<string, string | undefined>
    >({});


    // useEffect(() => {

    // }, [columnFilters])



    const columns = useMemo<MRT_ColumnDef<User>[]>(
        () => [
            {
                accessorKey: "id",
                header: "Id",
                enableEditing: false,

                minSize: 40,
                maxSize: 100,
                Cell: ({ row }) => row.index + 1, // 1-based counter

            },
            {
                accessorKey: "name",
                header: "Holiday Name",
                //  size: "auto",
                // minSize: 100,
                // maxSize: 200,
                muiEditTextFieldProps: {
                    required: true,
                    error: !!validationErrors?.name,
                    helperText: validationErrors?.name,
                    //remove any previous validation errors when user focuses on the input
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            name: undefined,
                        }),
                    //optionally add validation checking for onBlur or onChange
                },
                Cell: ({ renderedCellValue, row }) => {
                    const filterValue = columnFilters.find((f) => f.id === "name")?.value || "";

                    if (!filterValue) return renderedCellValue;

                    const regex = new RegExp(`(${filterValue})`, "gi");

                    // Ensure `renderedCellValue` is treated as a string
                    const parts = String(renderedCellValue).split(regex);

                    return (
                        <span>
                            {parts.map((part: string, index: number) =>
                                regex.test(part) ? (
                                    <span key={index} style={{ backgroundColor: "yellow", fontWeight: "bold" }}>
                                        {part}
                                    </span>
                                ) : (
                                    part
                                )
                            )}
                        </span>
                    );
                },

            },
            // {
            //   accessorKey: "lastName",
            //   header: "Last Name",

            //   muiEditTextFieldProps: {
            //     required: true,
            //     error: !!validationErrors?.lastName,
            //     helperText: validationErrors?.lastName,
            //     //remove any previous validation errors when user focuses on the input
            //     onFocus: () =>
            //       setValidationErrors({
            //         ...validationErrors,
            //         lastName: undefined,
            //       }),
            //   },
            // },
            // {
            //   accessorKey: "email",
            //   header: "Email",

            //   filterVariant: "select",
            //   filterSelectOptions: citiesList,
            //   muiEditTextFieldProps: {
            //     type: "email",
            //     required: true,
            //     error: !!validationErrors?.email,
            //     helperText: validationErrors?.email,
            //     //remove any previous validation errors when user focuses on the input
            //     onFocus: () =>
            //       setValidationErrors({
            //         ...validationErrors,
            //         email: undefined,
            //       }),
            //   },
            // },
            // {
            //   accessorKey: "state",
            //   header: "State",

            //   filterVariant: "multi-select",
            //   filterSelectOptions: usStateList,
            //   editVariant: "select",
            //   editSelectOptions: usStates,

            //   muiEditTextFieldProps: {
            //     select: true,
            //     error: !!validationErrors?.state,
            //     helperText: validationErrors?.state,
            //   },
            // },



        ],
        [validationErrors, columnFilters]
    );

    //call CREATE hook
    const { mutateAsync: createUser, isPending: isCreatingUser } =
        useCreateUser();
    //call READ hook


    const {
        data: fetchedHoliday = [],
        isError: isLoadingUsersError,
        isFetching: isFetchingUsers,
        isLoading: isLoadingUsers,
    } = useGetHoliday(columnFilters, sortingState);
    //call UPDATE hook
    const { mutateAsync: updateUser, isPending: isUpdatingUser } =
        useUpdateUser();
    //call DELETE hook
    const { mutateAsync: deleteUser, isPending: isDeletingUser } =
        useDeleteUser();

    //CREATE action
    const handleCreateUser: MRT_TableOptions<User>["onCreatingRowSave"] = async ({
        values,
        table,
    }) => {
        const newValidationErrors = validateUser(values);
        if (Object.values(newValidationErrors).some((error) => error)) {
            setValidationErrors(newValidationErrors);
            return;
        }
        setValidationErrors({});
        await createUser(values);
        table.setCreatingRow(null); //exit creating mode
    };

    //UPDATE action
    const handleSaveUser: MRT_TableOptions<User>["onEditingRowSave"] = async ({
        values,
        table,
    }) => {
        const newValidationErrors = validateUser(values);
        if (Object.values(newValidationErrors).some((error) => error)) {
            setValidationErrors(newValidationErrors);
            return;
        }
        setValidationErrors({});
        await updateUser(values);
        table.setEditingRow(null); //exit editing mode
    };

    //DELETE action
    const openDeleteConfirmModal = (row: MRT_Row<User>) => {
        showDeleteConfirmModal({
            title: "Delete Holiday Entry",
            content: `Are you sure you want to delete this user?`,
            onOk: () => {
                deleteUser(row.original.id);
            },
        });
    };

    let filterModes: Record<string, string> = {};
    const prevModeRef = useRef<any>(null);

    const table = useMaterialReactTable({
        columns,
        // enableColumnResizing:true,
        enableColumnFilters: true,
        data: fetchedHoliday,
        enableDensityToggle: false,
        enableColumnPinning: true,
        createDisplayMode: "row",
        editDisplayMode: "row",
        enableEditing: true,
        enableColumnOrdering: true,
        enableRowOrdering: false,
        enableClickToCopy: true,
        getRowId: (row) => row.id,
        enableColumnFilterModes: true,
        initialState: { showColumnFilters: true },
        filterFns: {
            customFilterFn: (row, id, filterValue) => {
                if (!filterValue || !Array.isArray(filterValue) || filterValue.length !== 2) {

                    return true; // If no filter or incorrect format, show all rows
                }

                const [min, max] = filterValue;
                const value: any = row.getValue(id);

                return value >= min && value <= max;
            },
        },
        onSortingChange: (updater) => {
            setSortingState((prevSorting) => {
                const newSorting = typeof updater === "function" ? updater(prevSorting) : updater;

                return newSorting;
            });
        },
        onColumnFiltersChange: (updater) => {
            setTrackFilter(!trackFilter)

            setColumnFilters((prevFilters) => {

                let newFilters = typeof updater === "function" ? updater(prevFilters) : updater;
                console.log("Before Update:", newFilters); // Debugging log
                console.log("prevFilters:", prevFilters); // Debugging log

                if (newFilters.length === 0 && prevFilters.length > 0) {
                    newFilters = prevFilters.map((filter) => ({
                        id: filter.id,
                        mode: prevModeRef.current?.[filter.id] || "custom",
                        value: filter.value,
                    }));
                }

                console.log(newFilters, "newFilters")


                const updatedFilters = newFilters.map((filter: any) => {
                    const column = table.getColumn(filter.id);
                    const filterFn = column?.getFilterFn();

                    const newMode = filterFn ? filterFn.name : "unknown";

                    return {
                        id: filter.id,
                        mode: newMode, // ✅ Update only the mode
                        value: filter.value, // ✅ Preserve value
                    };
                });

                // ⚡️ Update ref BEFORE setting state
                prevModeRef.current = Object.fromEntries(updatedFilters.map(f => [f.id, f.mode]));

                console.log("After Update:", updatedFilters); // Debugging log

                return updatedFilters;
            });
        },








        muiTablePaperProps: {
            elevation: 0,
            sx: {
                borderRadius: "0",
                border: "0",
            },
        },
        localization: {
            filterCustomFilterFn: "Custom Filter Fn",
        } as any,

        muiToolbarAlertBannerProps: isLoadingUsersError
            ? {
                color: "error",
                children: "Error loading data",
            }
            : undefined,
        muiTableContainerProps: {
            sx: {
                minHeight: "500px",
            },
        },

        onCreatingRowCancel: () => setValidationErrors({}),
        onCreatingRowSave: handleCreateUser,
        onEditingRowCancel: () => setValidationErrors({}),
        onEditingRowSave: handleSaveUser,
        renderRowActions: ({ row, table }) => (
            <Box sx={{ display: "flex", gap: "1rem" }}>
                <Tooltip title="Edit">
                    <IconButton onClick={() => table.setEditingRow(row)}>
                        <EditIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                    <IconButton color="error" onClick={() => openDeleteConfirmModal(row)}>
                        <DeleteIcon />
                    </IconButton>
                </Tooltip>
            </Box>
        ),

        renderTopToolbarCustomActions: ({ table }) => (
            <>
                <TextField
                    label="Broker Name"
                    variant="outlined"
                    size="small"
                    style={{ marginRight: "10px" }}
                    onChange={(e) => console.log("Broker Name:", e.target.value)}
                />

                <Button
                    variant="contained"
                    onClick={() => {
                        table.setCreatingRow(true);
                    }}
                >
                    Create New User
                </Button>
            </>
        ),


        state: {
            columnFilters,
            isLoading: isLoadingUsers,
            isSaving: isCreatingUser || isUpdatingUser || isDeletingUser,
            showAlertBanner: isLoadingUsersError,
            showProgressBars: isFetchingUsers,
            showColumnFilters: true,
            sorting: sortingState,

        },
    });

    useEffect(() => {
        if (!table) return; // Avoid errors if table is undefined

        setColumnFilters((prevFilters) => {



            if (prevFilters.length === 0) return prevFilters; // Prevent unnecessary updates

            const updatedFilters = prevFilters.map((e) => ({
                ...e,
                mode: table.getColumn(e.id)?.getFilterFn()?.name || "unknown",
            }));

            // 🛑 Check if the new filters are different before updating state
            if (JSON.stringify(updatedFilters) === JSON.stringify(prevFilters)) {
                return prevFilters; // No change, avoid updating state
            }

            // Update the mode reference
            prevModeRef.current = Object.fromEntries(updatedFilters.map(f => [f.id, f.mode]));

            return updatedFilters;
        });
    }, [table, trackFilter]); //


    return (
        <div className="custom_tableGrid">
            <div>form</div>
            <div className="custom_tableGrid__wrapper">

                <MaterialReactTable table={table} />
            </div>
        </div>
    );
};

//CREATE hook (post new user to api)
function useCreateUser() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (user: User) => {
            //send api update request here
            await new Promise((resolve) => setTimeout(resolve, 1000)); //fake api call
            return Promise.resolve();
        },
        //client side optimistic update
        onMutate: (newUserInfo: User) => {
            queryClient.setQueryData(
                ["users"],
                (prevUsers: any) =>
                    [
                        ...prevUsers,
                        {
                            ...newUserInfo,
                            id: (Math.random() + 1).toString(36).substring(7),
                        },
                    ] as User[]
            );
        },
        // onSettled: () => queryClient.invalidateQueries({ queryKey: ['users'] }), //refetch users after mutation, disabled for demo
    });
}





function useGetHoliday(columnFilters: MRT_ColumnFiltersState, sortingState: any) {

    return useQuery<User[]>({
        queryKey: ["users", JSON.stringify(columnFilters), JSON.stringify(sortingState)],
        queryFn: async () => {
            try {
                const params = new URLSearchParams();

                columnFilters.forEach((filter: any) => {

                    params.append(`filter_${filter.id}`, String(filter.value));

                    if (filter.mode) {
                        params.append(`filterMode_${filter.id}`, filter.mode);
                    }


                });
                if (sortingState.length > 0) {
                    sortingState.forEach((sort: any) => {
                        params.append("sort_by", sort.id); // Column name
                        params.append("sort_order", sort.desc ? "desc" : "asc"); // Sorting order
                    });
                }


                const response = await axiosInstance.get(
                    `${backendbaseUrl}/paper-app/holiday/?${params.toString()}`
                );

                return response.data.results;
            } catch (error) {
                throw new Error("Failed to fetch users");
            }
        },
        refetchOnWindowFocus: false,
    });
}


//UPDATE hook (put user in api)
function useUpdateUser() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (user: User) => {
            //send api update request here
            await new Promise((resolve) => setTimeout(resolve, 1000)); //fake api call
            return Promise.resolve();
        },
        //client side optimistic update
        onMutate: (newUserInfo: User) => {
            queryClient.setQueryData(["users"], (prevUsers: any) =>
                prevUsers?.map((prevUser: User) =>
                    prevUser.id === newUserInfo.id ? newUserInfo : prevUser
                )
            );
        },
        // onSettled: () => queryClient.invalidateQueries({ queryKey: ['users'] }), //refetch users after mutation, disabled for demo
    });
}

//DELETE hook (delete user in api)
function useDeleteUser() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (userId: string) => {
            //send api update request here
            await new Promise((resolve) => setTimeout(resolve, 1000)); //fake api call
            return Promise.resolve();
        },
        //client side optimistic update
        onMutate: (userId: string) => {
            queryClient.setQueryData(["users"], (prevUsers: any) =>
                prevUsers?.filter((user: User) => user.id !== userId)
            );
        },
        // onSettled: () => queryClient.invalidateQueries({ queryKey: ['users'] }), //refetch users after mutation, disabled for demo
    });
}

//react query setup in App.tsx
const ReactQueryDevtoolsProduction = lazy(() =>
    import("@tanstack/react-query-devtools/build/modern/production.js").then(
        (d) => ({
            default: d.ReactQueryDevtools,
        })
    )
);

const queryClient = new QueryClient();

export default function App() {
    return (
        <Container fluid>
            <Row>

                <Col sm={12}>
                    <Card>
                        <PaperCardHeader headClass='sss' title='ssss' subTitle='ssss' titleClass='sss' />
                        <QueryClientProvider client={queryClient}>
                            <HolidayComponent />
                            <Suspense fallback={null}>
                                <ReactQueryDevtoolsProduction />
                            </Suspense>
                        </QueryClientProvider>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
}

const validateRequired = (value: string) => !!value.length;
const validateEmail = (email: string) =>
    !!email.length &&
    email
        .toLowerCase()
        .match(
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        );

function validateUser(user: User) {
    return {
        firstName: !validateRequired(user.firstName)
            ? "First Name is Required"
            : "",
        lastName: !validateRequired(user.lastName) ? "Last Name is Required" : "",
        email: !validateEmail(user.email) ? "Incorrect Email Format" : "",
    };
}

// function setSortingState(arg0: (prevSorting: any) => import("@tanstack/table-core").SortingState) {
//     console.log("khk")
//     return true
// }

